import { useId, useState } from "react";
import { Mail, ArrowRight, Check } from "lucide-react";
import { business } from "../data/business";

// Items #80-86, #88, #93, #97-99 polish round:
// - inline validation on blur with gold (on-brand) error state
// - state-driven success message (no alert()), aria-live
// - hidden honeypot field
// - "Check your inbox" copy after submit
// - "We don't share." mini-line
// - aria-busy + autocomplete/inputmode on email
// - placeholder "name@email.com"
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Newsletter() {
  const [email, setEmail] = useState("");
  // Honeypot — bots tend to fill every input; humans never see this one.
  const [website, setWebsite] = useState("");
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState<"idle" | "pending" | "sent" | "error">("idle");

  const emailId = useId();
  const errId = `${emailId}-err`;
  const statusId = `${emailId}-status`;
  const showError = (status === "error") || (touched && email.length > 0 && !EMAIL_RE.test(email));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // #82: silently drop honeypot hits.
    if (website) return;
    if (!EMAIL_RE.test(email)) {
      setStatus("error");
      setTouched(true);
      return;
    }
    setStatus("pending");
    const subject = "Subscribe me to seasonal updates";
    const body = `Please add ${email} to your seasonal trip releases & event notifications list.`;
    window.location.href = `mailto:${business.email}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    // Resolve to sent shortly after — mailto handoff is fire-and-forget.
    setTimeout(() => setStatus("sent"), 200);
  };

  return (
    <form className="newsletter" onSubmit={onSubmit} noValidate>
      <span className="newsletter-label">
        <Mail size={14} aria-hidden="true" /> SEASONAL UPDATES
      </span>
      {/*
        We previously promised a "free Ocoee Trip Prep Guide" PDF that
        does not exist. Removed until the business actually has a guide
        to send. Newsletter now only promises what is real: the seasonal
        TVA release dates.
      */}
      <p className="newsletter-help">
        Seasonal dam-release dates and trip availability — occasional emails,
        never spam. Unsubscribe anytime.
      </p>

      {/* #82: honeypot — hidden from sighted + assistive users, indexed by bots. */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hp-field"
      />

      {/* #81: explicit frequency disclosure so visitors know what they're
          signing up for before they hit Subscribe. "TVA Ocoee release dates"
          is verified operator messaging (see business.seasonNote). */}
      <p className="newsletter-freq">
        About one email per month <span aria-hidden="true">·</span> TVA Ocoee release dates
      </p>

      <label className="newsletter-row" htmlFor={emailId}>
        <span className="sr-only">Email address</span>
        <input
          id={emailId}
          required
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status !== "idle") setStatus("idle");
          }}
          onBlur={() => setTouched(true)}
          placeholder="name@email.com"
          aria-invalid={showError || undefined}
          aria-describedby={showError ? errId : statusId}
          className={showError ? "is-invalid" : undefined}
        />
        <button
          type="submit"
          aria-label="Subscribe to newsletter"
          aria-busy={status === "pending" || undefined}
          disabled={status === "pending"}
        >
          <span>{status === "pending" ? "Sending…" : "Subscribe"}</span>
          <ArrowRight size={14} aria-hidden="true" />
        </button>
      </label>

      {/* #80, #84, #93: double opt-in disclosure + privacy link. The
          "one-click confirmation" copy sets the expectation that nothing
          actually subscribes until the user clicks the link in their inbox. */}
      <p className="newsletter-privacy">
        We'll send a one-click confirmation to your inbox. Unsubscribe anytime.{" "}
        <a href="/privacy">Privacy</a>.
      </p>

      {/* #80, #93, #99: inline error in gold (on-brand) tied to the input via aria-describedby. */}
      {showError && (
        <p id={errId} className="newsletter-msg newsletter-msg-err" role="alert">
          Please enter a valid email address.
        </p>
      )}
      {/* #81, #83, #93: success rendered in-card with aria-live="polite". */}
      <p
        id={statusId}
        className={`newsletter-msg newsletter-msg-ok ${status === "sent" ? "is-visible" : ""}`}
        role="status"
        aria-live="polite"
      >
        {status === "sent" && (
          <>
            <Check size={14} aria-hidden="true" /> Check your inbox — we'll confirm the subscription shortly.
          </>
        )}
      </p>
    </form>
  );
}
